import logger from '../utils/logger-rum';
/* eslint-disable no-restricted-globals */
import { CSSProperties } from 'react';
import { Cookies } from 'react-cookie';
import { Publisher } from '@opentok/client';
import {
	BROWSER_TYPES,
	DURATION_INPUT_TYPES, VID_TYPES,
	SUPPORTED_CHAT_FILE_FORMATS,
	MAX_FILE_SIZE,
	MEDIA_TYPES, AUDIO_TYPES,
} from '../types/constants';
import randomNames from './random-names';
import colors from './colors';
import type { Invitation, Profile } from '../stores/types/invitations';
import { clearStorage } from './local-storage';
import { ConnectedUser } from '../stores/types/connection';
import type { GreenroomUser, Meeting } from '../stores/types/session';
import {
	CssPosition,
	IDateObjectProps,
	VideoRect,
} from '../stores/types/types';
import { S3_SUB_DIRECTORY } from '../connection/media';
import type { MediaItem } from '../stores/types/media';
import { AvatarUser } from '../components/general-components/avatar';
import type {
	DisplayChatMessage,
	EventsMessageChat,
	EventsMessageComment,
	GeneralEventsMessage,
} from '../stores/types/chat';
import { EMessageTypes } from '../stores/types/chat';
import type { Channel } from '../stores/types/home';
import V_LAYOUTS, { PIP_LAYOUTS, PLAIN_LAYOUTS } from './video-layout';
import type { AppSettings } from '../stores/types/settings';

export const COOKIE_NAMES = {
	CURRENT_USER: 'currentUser',
	AUDIENCE_MEMBER: 'brandlive_uid',
	TWITTER_ACCESS_TOKEN: 'twitterAccessToken',
	TWITTER_ACCESS_TOKEN_SECRET: 'twitterAccessTokenSecret',
	REGISTRATION_NAME_LOCATION: 'registrationNameAndLocation',
};

export const DOMAINS = {
	BRANDLIVE: process.env.REACT_APP_DOMAIN,
};

export function isProduction(): boolean {
	if (
		typeof window !== 'undefined'
		&& window.location.href.indexOf('localhost') === -1
	) {
		return true;
	}
	return false;
}

export function getExpirationDate(hours: number): Date {
	const now = Date.now();
	const expirationMs = 1000 * 60 * 60 * hours;
	return new Date(now + expirationMs);
}

export function setCookie(
	cookies: Cookies,
	name: string,
	value: string,
	expiresDate: Date,
	domain: string,
): void {
	if (isProduction()) {
		if (expiresDate) {
			// console.log('setting with expires date ' + expiresDate + ' to domain ' + domain);
			cookies.set(
				name,
				value,
				{ path: '/', expires: expiresDate, domain },
			);
		} else {
			cookies.set(name, value, { path: '/', domain });
		}
	} else if (expiresDate) {
		cookies.set(name, value, { path: '/', expires: expiresDate });
	} else {
		cookies.set(name, value, { path: '/' });
	}
}

export function removeCookie(cookies: Cookies, name: string, domain: string): void {
	if (isProduction()) {
		cookies.remove(name, { path: '/', domain });
	} else {
		cookies.remove(name, { path: '/' });
	}
}

export function getChatCookieName(liveChat: number): string {
	return `chat[${liveChat}]`;
}

export function getImageLocation(uri: string): string | undefined {
	let image = '';
	if (uri?.includes('assets/') && !uri?.includes('https://')) {
		image = `https://brandlive.com/${uri}`;
	} else if (!uri.includes('http')) {
		image = `https://greenroom-assets-prod.brand.live/legacy-uploads/${uri}`;
	} else {
		image = uri;
	}

	if (uri) {
		return image;
	}
	return undefined;
}

// Opens a popup window in the center of the screen. The reason it opens a _blank window
// and returns the window instead of going straight to the destination URL is because of
// popup blockers. For twitter login we tried opening a popup window in the return
// function of the request-token API call but the window would get blocked because
// it wasn't being opened in a direct user action (button click, link click, etc.
// This is standard browser popup security)
export function popupCenter(w: number, h: number): Window | null {
	const left = (screen.width / 2) - (w / 2); // eslint-disable-line no-restricted-globals
	const top = (screen.height / 2) - (h / 2); // eslint-disable-line no-restricted-globals

	const newWindow: Window | null = window.open('', '_blank', `scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`);

	// Puts focus on the newWindow
	if (newWindow && newWindow.focus) newWindow.focus();
	return newWindow;
}

export function getAudienceMemberSessionCookieName(liveChat: number): string {
	return `session_${liveChat}`;
}

// This function will validate if Rtmp Address starts with rtmp:// or rtmps://
export function isValidRtmpAddress(rtmpAddress: string): boolean {
	const pattern = '^\\s*(rtmp|rtmps)://';
	const addressRegex = new RegExp(pattern, 'i');
	return addressRegex.test(rtmpAddress);
}

export function isValidEmail(email: string): boolean {
	// eslint-disable-next-line no-useless-escape
	const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(String(email).toLowerCase());
}

export function isSafeUrl(url: string): boolean {
	try {
		const { protocol } = new URL(url);
		return protocol === 'https:' || protocol === 'http:';
	} catch {
		return false;
	}
}

export function isValidPassword(password: string): boolean {
	const hasEightChars = () => password.length >= 8;

	const hasOneLowercase = () => (/[a-z]/.test(password));

	const hasOneUpperCase = () => (/[A-Z]/.test(password));

	const hasOneNumber = () => (/[0-9]/.test(password));

	return hasEightChars() && hasOneLowercase() && hasOneUpperCase() && hasOneNumber();
}

export function getProfileImageURL(item: any): string {
	let picture = '';
	if (item.picture) {
		picture = item.picture;
	} else if (item.image) {
		picture = item.image;
	} else if (item.profile_picture) {
		picture = item.profile_picture;
	}
	return picture;
}

export function getFullAssetURI(asset: string | undefined): string {
	if (!asset) {
		return '';
	} if (
		asset.startsWith('http://')
		|| asset.startsWith('https://')
		|| asset.startsWith('//')
	) {
		return asset;
	} if (asset.startsWith('/assets/')) {
		return `https://cdn.brnd.live${asset}`;
	} if (asset.startsWith('assets/')) {
		return `https://brandlive.com/${asset}`;
	}
	return `https://greenroom-assets-prod.brand.live/legacy-uploads/${asset}`;
}

// Refactored this function from chat-document-thumbnail and media-modal before
// I saw the one above ^.
// Might want to refactor both of these together.
export function getAssetUri(uri: string): string {
	if (!uri || uri === '') return '';
	if (uri?.includes('0/')) {
		return `https://greenroom-assets-prod.brand.live/legacy-uploads/${uri}`;
	}
	return `https://greenroom-assets-prod.brand.live/legacy-uploads/0/${uri}`;
}

function hexToArray(hex: string): number[] {
	let newHex = hex;
	if (newHex?.includes('#')) {
		newHex = newHex.substr(1, newHex.length);
	}

	const chars = newHex.split('');

	const r = chars[0] + chars[1];
	const g = chars[2] + chars[3];
	const b = chars[4] + chars[5];

	return [
		parseInt(r, 16),
		parseInt(g, 16),
		parseInt(b, 16),
		255,
	];
}

// calculates luminance https://en.wikipedia.org/wiki/Luma_(video)
function avg(pixelData: Uint8ClampedArray | number[]): number {
	// pixelData is a UInt8Array

	// loop through array
	const len = pixelData.length;
	let totLuma = 0;
	let avgVariable = 0;
	const rgb = [];

	for (let i = 0; i < len; i += 1) {
		// the color values come in as a single array, but the individual pixel values are quads
		// so arrayed like this: [r, g, b, a, r, g, b, a, r, g, b, a...]
		// we want to ignore the alpha channel, so we create a temp array to hold
		// the rgb values, then whenever this array hits the alpha number, calculate the
		// luma of the pixel data without doing anything with the alpha number (so each 4th
		// iteration, ignore the value and calculate the luminance)
		if (((i + 1) % 4) > 0) {
			rgb[i + 1 % 4] = pixelData[i]; // eslint-disable-line no-mixed-operators
		} else {
			const R = rgb[1];
			const G = rgb[2];
			const B = rgb[3];

			// fast formula found here https://stackoverflow.com/questions/596216/formula-to-determine-brightness-of-rgb-color
			totLuma += (R + R + R + B + G + G + G + G) >> 3; // eslint-disable-line no-bitwise
		}
	}

	// get average color of image
	avgVariable = totLuma / (len / 4);
	return avgVariable;
}

export function hexToRgb(hex: string): number[] {
	const newHex = hex.replace(/#/g, '');
	const bigint = parseInt(newHex, 16);
	const r = (bigint >> 16) & 255; // eslint-disable-line no-bitwise
	const g = (bigint >> 8) & 255; // eslint-disable-line no-bitwise
	const b = bigint & 255; // eslint-disable-line no-bitwise

	return [r, g, b];
}

let textColor: string | null = null;

export function contrastingColor(color: string): string {
	let newColor = color;
	if (newColor.startsWith('#')) {
		newColor = newColor.substr(1, color.length);
	}

	const chars = newColor.split('');
	const R = parseInt(chars[0] + chars[1], 16);
	const G = parseInt(chars[2] + chars[3], 16);
	const B = parseInt(chars[4] + chars[5], 16);
	const total = (0.2126 * R) + (0.7152 * G) + (0.0722 * B);
	if (total > 128) return colors.darkerGray;
	return colors.nearWhite;
}

export function getContrastingColor(theme: any): Promise<string | null> {
	return new Promise((resolve) => {
		// to keep this from being recalculated every state update, just do it once,
		// the background image is static
		if (textColor) {
			resolve(textColor);
			return;
		}

		// if the background color and header background color is not set but a background image is set
		if (
			!theme.background_color_override
			&& !theme.header_background_color_override
			&& theme.background_image
		) {
			try {
				// create an image from the background_image
				const img = document.createElement('img');

				// needed to prevent CORS error in admin console
				img.crossOrigin = 'Anonymous';

				// when image loads
				img.onload = () => {
					// create a canvas, set to the size of the image
					const canvas = document.createElement('canvas');
					canvas.width = img.width;
					canvas.height = img.height;

					// draw the image to the canvas
					const ctx = canvas.getContext('2d');
					if (ctx) {
						ctx.drawImage(img, 0, 0, img.width, img.height);

						// we want to measure a 20x20px box 30px inset from the top right edge
						const xStart = img.width - 50;

						// gets average luminance
						const luminance = avg(ctx.getImageData(xStart, 30, 20, 20).data);

						if (luminance > 128) {
							textColor = '#4A4A4A';
						} else {
							textColor = '#ffffff';
						}

						resolve(textColor);
					} else {
						resolve(null);
					}
				};

				// load image
				img.src = theme.background_image;
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e);
				resolve(null);
			}
		} else if (theme.background_color_override && !theme.header_background_color_override) {
			try {
				const luminance = avg(hexToArray(theme.background_color));

				if (luminance > 128) {
					textColor = '#4A4A4A';
				} else {
					textColor = '#ffffff';
				}

				resolve(textColor);
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error(e);
				resolve(null);
			}
		} else {
			resolve(null);
		}
	});
}

export function urlLoads(url: string): Promise<boolean> {
	return fetch(url).then((res) => {
		if (res.status === 404) {
			return false;
		}
		return true;
	}).catch((e) => {
		// eslint-disable-next-line no-console
		console.error(e);
		return false;
	});
}

export function getRandomName(): string {
	const animalLength = randomNames.animals.length;
	const adjectiveLength = randomNames.adjectives.length;

	return `${randomNames.adjectives[Math.floor(Math.random() * adjectiveLength)]} ${randomNames.animals[Math.floor(Math.random() * animalLength)]}`;
}

/**
 *
 * @param key key name of item in array you want to be unique
 * @param arr array you want to dump duplicates from
 */
export function toMap(key: string, arr: any[]): Map<string, any> {
	return new Map(arr.filter((item: any) => item[key]).map((item: any) => [item[key], item]));
}

export function uniqueArray(key: string, arr: any[]): any[] {
	return Array.from(toMap(key, arr).values());
}

export function mergeObjectArray(key: string, orderKey: string, arr: any[]): any[] {
	const map: any = {};

	arr.forEach((item: any) => {
		let currentFirst = true;
		const currentItem = map[item[key]];

		if (currentItem) {
			if (Number.isNaN(Date.parse(item[orderKey]))
				&& Number.isNaN(Date.parse(currentItem[orderKey]))
				&& Date.parse(item[orderKey]) > Date.parse(currentItem[orderKey])) {
				currentFirst = false;
			} else if (
				item[orderKey]
				&& currentItem[orderKey]
				&& item[orderKey] > currentItem[orderKey]
			) {
				currentFirst = false;
			}

			if (currentFirst) {
				map[currentItem[key]] = { ...currentItem, ...item };
			} else {
				map[currentItem[key]] = { ...item, ...currentItem };
			}
		} else {
			map[item[key]] = item;
		}
	});

	return Array.from(Object.values(map));
}

interface Hashmap {
	[key: string]: any;
}

export function toHashmap(key: string, arr: any[]): Hashmap {
	const hash: Hashmap = {};
	const len = arr.length;

	for (let i = 0; i < len; i += 1) {
		if (arr[i][key]) {
			hash[arr[i][key]] = arr[i];
		}
	}

	return hash;
}

export function countTimer(totalSeconds: number, timeText: any) {
	const realTotalSeconds = totalSeconds + 1;
	let hour = Math.floor(realTotalSeconds / 3600) as any;
	let minutes = Math.floor((realTotalSeconds - hour * 3600) / 60) as any;
	let seconds = realTotalSeconds - (hour * 3600 + minutes * 60) as any;
	if (hour < 10) { hour = `0${hour}`; }
	if (minutes < 10) {
		minutes = `0${minutes}`;
	}
	if (seconds < 10) {
		seconds = `0${seconds}`;
	}

	timeText.innerHTML = `${minutes} : ${seconds}`; // eslint-disable-line no-param-reassign
	// eslint-disable-next-line no-console
	console.log(`test ${`${minutes} : ${seconds}`}`);
	return realTotalSeconds;
}

export function getVideoPoster(url: string): string | undefined {
	let poster = '';
	if (url.endsWith('-hls/master.m3u8')) {
		poster = url.replace(/(-mov-hls\/master.m3u8|-mp4-hls\/master.m3u8|-hls\/master.m3u8)$/, '.jpg');
	} else {
		poster = url.endsWith('.webm') ? url.replace(/(.webm)$/, '.png') : url?.replace(/(.mp4|.mov)$/, '.jpg');
	}

	poster = poster.split('/').map((item: string, index: number, arr: string[]) => {
		if (index === arr.length - 1) {
			return `${S3_SUB_DIRECTORY.POSTER}/${item}`;
		}
		return item;
	}).join('/');
	return poster ?? undefined;
}

export function extractBitrate(input: string): number {
	const result = input.match(/bitrate=(.*?)kbits\/s/);
	if (result) {
		return Number(result[1]);
	}
	return 0;
}

export const parseBitrate = (bitrate: number, includeSpace: boolean) => {
	if (!bitrate) return '';
	const sizes = ['kbps', 'mbps'];
	const i = Math.floor(Math.log(bitrate) / Math.log(1000));
	return parseFloat((bitrate / 1000 ** i).toFixed(1)) + (includeSpace ? ' ' : '') + sizes[i];
};

export function extractFps(input: string): number {
	const result = input.match(/fps= (.*?) /);
	if (result) {
		return Number(result[1]);
	}
	return 0;
}

export function extractHealth(input: string): number {
	const result = input.match(/speed=(.*)x/);
	if (result) {
		return Number(result[1]);
	}
	return 0;
}

export function sleep(ms: number) {
	const sleepyTimmy = new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
	return sleepyTimmy;
}

function castAsNumber(str: string) {
	return Number(str);
}

export function listToArray(list: string): number[] {
	const spl = list.split(',');
	return spl.map(castAsNumber);
}

export function missingPasswordError(error: string) {
	if (error === 'nopassword') {
		return 'It looks like you need to set a password for this account. Please click on “Forgot Password” to set one.';
	}
	return `Looks like you've attempted to sign in with ${error}. Please follow the "Forgot Password" link to set a password for this account.`;
}

export function upcomingDateTime(
	startTime: number,
	endTime: number,
	meridiem: boolean,
	bulletAndEmdash: boolean,
): string {
	const start = new Date(startTime);
	const end = new Date(endTime);
	const eventStartDate = start.toLocaleDateString(navigator.language, { weekday: 'short', month: 'short', day: 'numeric' });
	const eventStartTime = start.toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit' });
	const eventEndDate = end.toLocaleDateString(navigator.language, { weekday: 'short', month: 'short', day: 'numeric' });
	const eventEndTime = end.toLocaleTimeString(navigator.language, { hour: 'numeric', minute: '2-digit' });
	const dot = bulletAndEmdash ? '•' : '⋅';
	const dash = bulletAndEmdash ? '—' : '-';
	if (eventStartDate === eventEndDate && meridiem) {
		return `${eventStartDate} ${dot} ${eventStartTime} ${dash} ${eventEndTime}`;
	} if (eventStartDate === eventEndDate && !meridiem) {
		return `${eventStartDate} ${dot} ${eventStartTime.replace('AM', '').replace('PM', '')} ${dash} ${eventEndTime}`;
	}
	return `${eventStartDate} ${dot} ${eventStartTime} ${dash} ${eventEndDate} ${dot} ${eventEndTime}`;
}

export function cardBackground(meeting: any, TellyCard: any, gradient: boolean, featured: boolean) {
	if (!featured) {
		return {
			backgroundImage: '',
			backgroundColor: '',
			backgroundSize: '',
		};
	}

	const applyGradient = gradient ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(9, 9, 10, 0.2) 0.01%, rgba(9, 9, 10, 0.7) 100%), ' : '';
	const streamsImage = meeting?.thumbnail;
	const meetingImage = meeting?.settings_json
		? JSON.parse(meeting.settings_json)?.defaultMeetingImage
		: undefined;

	if (streamsImage) {
		return {
			backgroundImage: `${applyGradient}url(${streamsImage})`,
			backgroundColor: `${meetingImage?.color}`,
			backgroundSize: 'cover',
		};
	}

	return meetingImage && (meetingImage.image || meetingImage.color) ? {
		backgroundImage: `${applyGradient}url(${meetingImage.image ? meetingImage.image : ''})`,
		backgroundColor: `${meetingImage.color}`,
		backgroundSize: gradient ? `100%, ${meetingImage.scale}%` : `${meetingImage.scale}%`,
	} : {
		backgroundImage: `${applyGradient}url(${TellyCard})`,
		backgroundSize: gradient ? '100%, 100%' : '100%',
	};
}

export function numberToPercent(num: number): string {
	return `${num * 0.2}%`;
}

export function percMultiply(percentage: string) {
	const num = Number(percentage.replace(/%/g, ''));
	return `${num * (16 / 9)}%`;
}

// takes a duration in seconds and returns a string formatted to MM:SS
// also takes an optional boolean to return string formatted to HH:MM:SS
export function durationString(
	durationInSeconds: number,
	includeHours?: boolean,
	spaced?: boolean,
) {
	const minutes = Math.floor(durationInSeconds / 60);
	const remainingSeconds = Math.trunc(durationInSeconds % 60);
	const options = { minimumIntegerDigits: 2 };
	const formattedMinutes = minutes.toLocaleString('en-US', options);
	const formattedRemainingSeconds = remainingSeconds.toLocaleString('en-US', options);

	if (durationInSeconds >= (60 * 60) || includeHours) {
		const hours = Math.floor(minutes / 60);
		const remainingMinutes = minutes % 60;
		const formattedHours = hours.toLocaleString('en-US', options);
		const formattedRemainingMinutes = remainingMinutes.toLocaleString('en-US', options);
		return spaced
			? `${formattedHours} : ${formattedRemainingMinutes} : ${formattedRemainingSeconds}`
			: `${formattedHours}:${formattedRemainingMinutes}:${formattedRemainingSeconds}`;
	}
	return spaced
		? `${formattedMinutes} : ${formattedRemainingSeconds}`
		: `${formattedMinutes}:${formattedRemainingSeconds}`;
}

export const getVideoTimeAsHHMMSS = (totalSeconds: number) => {
	if (!totalSeconds) { return '0:00'; }

	const hour = Math.floor(totalSeconds / 3600) as string | number;
	let min = Math.floor(totalSeconds % 3600 / 60) as string | number; // eslint-disable-line
	let sec = Math.floor(totalSeconds % 3600 % 60) as string | number;

	// minutes only show leading zeros in video timers if there's an hour slot
	//	correct 	3:07 / 1:23:59
	//				1:03:07 / 1:23:59
	//  incorrect 	03:07 / 1:23:59
	// @ts-ignore
	if (min < 10 && hour > 0) {
		min = `0${min}`;
	}

	// seconds always show leading 0's because min's always show, even if mins is 0
	// @ts-ignore
	if (sec < 10) { sec = `0${sec}`; }
	// ex 0:07 / 0:45

	// hours never show leading zeros in video time ui's
	// correct		1:08:44	/ 14:23:07 (14hr video god help us)
	// incorrect		01:08:44 / 14:23:07

	return `${Number(hour) ? `${hour}:` : ''}${min}:${sec}`;
};

interface UNames {first_name?: string | null, last_name?: string | null, email?: string}
export function getNameOrInitials(
	user: UNames | null | undefined,
	name: boolean,
) {
	const firstName = user?.first_name?.trim();
	const lastName = user?.last_name?.trim();
	const email = user?.email?.trim();

	if (firstName && lastName) {
		return name ? `${firstName} ${lastName}` : `${firstName[0]}${lastName[0]}`;
	} if (firstName) {
		return name ? firstName : firstName[0];
	} if (lastName) {
		return name ? lastName : lastName[0];
	} if (email) {
		return name ? email : email[0];
	}
	return '';
}

export function getPosterImageFromProfile(user: {poster?: string | null, avatar?: string | null}): string | undefined {
	if (!user) return;
	if (user.poster) return user.poster;
	if (user.avatar) return user.avatar;
	return undefined;
}

export async function logOut(isProducer: boolean, accountDeleted?: boolean) {
	await clearStorage();
	// When we delete an account we display a splash screen prior to navigating back to login page
	if (!accountDeleted) {
		const url = new URL(window.location.href);
		// Remove anything extra from the URL like meeting_id, invite, etc. if it existed
		const updatedUrl = `${url.protocol}//${url.host}`;
		if (isProducer) {
			window.location.href = `${updatedUrl}/producer`;
		} else {
			window.location.href = updatedUrl;
		}
	}
}

export function stripHtml(message: string) {
	const strippedBreaks = message.replace(/<br \/>/g, ' ');
	const cleanedText = new DOMParser().parseFromString(strippedBreaks, 'text/html');
	return cleanedText.body.textContent || '';
}

export function isLocalUser(user: GreenroomUser, userId: any) {
	return user.gr_user === Number(userId) || user.brandlive_profile === Number(userId);
}

export function streamIsMobile(stream: any): boolean {
	return stream?.name?.includes('mob-');
}

export function streamIsScreenshare(eventTarget: any): boolean {
	return eventTarget instanceof Publisher
		|| eventTarget.stream?.channel[0]?.source === 'screen';
}

export function getUserIdFromMobileStream(stream: any) {
	return stream?.name?.replace(/mob-/, '');
}

/**
 * Extract the presenter profile ID from a stream's name.
 * Mobile streams use "mob-{id}", regular streams use "{id},{guid}".
 */
export function getProfileFromStreamName(streamName: string): number {
	const isMobile = streamName.includes('mob-');
	return isMobile
		? Number(streamName.replace(/mob-/, ''))
		: Number(streamName.split(',')[0]);
}

/**
 * Detect if a stream is a screenshare by channel source or SynergySky naming.
 * Unlike streamIsScreenshare, this works with raw stream objects (not event targets).
 */
export function isStreamScreenshare(stream: any): boolean {
	const name = stream?.name || '';
	return stream?.channel?.[0]?.source === 'screen'
		// The same value as channel[0].source, on stream objects that expose it
		// as videoType rather than carrying the raw channel.
		|| stream?.videoType === 'screen'
		|| name.includes('synergysky-screen')
		|| name.includes('secondary-screen');
}

/**
 * Known fields found in OpenTok stream connection data.
 * The index signature allows forward-compatible access to new fields.
 */
export interface StreamConnectionData {
	sip?: boolean;
	onsiteLocationId?: number;
	name?: string;
	[key: string]: unknown;
}

/**
 * Parse connection data from a stream. Returns empty object on failure.
 */
export function parseStreamConnectionData(stream: any): StreamConnectionData {
	try {
		return JSON.parse(stream?.connection?.data ?? '{}');
	} catch {
		return {};
	}
}

export function getUserIdFromMobileName(name: string) {
	return name.replace(/mob-/, '');
}

export function createUUID(): string {
	// @ts-ignore
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
		// tslint:disable-next-line:no-bitwise
		(c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)); // eslint-disable-line
}

export function capitalize(word: String): String {
	if (word.length === 1) {
		return word.toUpperCase();
	}
	if (word.length) {
		return word[0].toUpperCase() + word.substring(1);
	}

	return word;
}

export function convertPxToRem(pixelToConvert: number): number {
	const baseAppFontSize = parseFloat(window.getComputedStyle(document.body).getPropertyValue('font-size'));
	if (pixelToConvert > 0) return pixelToConvert / baseAppFontSize;
	return 0;
}

export function getVideoRect(wrapWidth: number, wrapHeight: number): VideoRect {
	let vidWidth = wrapWidth;
	let vidHeight = wrapWidth * (9 / 16); // or 0.5625

	if (vidHeight > wrapHeight) {
		vidHeight = wrapHeight;
		vidWidth = wrapHeight * (16 / 9);
	}

	const top = (wrapHeight - vidHeight) / 2;
	const left = (wrapWidth - vidWidth) / 2;

	return {
		width: `${Math.floor(vidWidth)}px`,
		height: `${Math.floor(vidHeight)}px`,
		top: `${Math.floor(top)}px`,
		left: `${Math.floor(left)}px`,
	};
}

export function invokeAfter(this: any, func: (() => void) | null, delay: number): () => void {
	let cancel = () => { };
	const browser: any = window;
	if (func) {
		if (!(browser && (
			browser.requestAnimationFrame
			|| (browser.mozRequestAnimationFrame && browser.mozCancelRequestAnimationFrame)
			|| browser.oRequestAnimationFrame
			|| browser.msRequestAnimationFrame))) {
			const invokeTo = setTimeout.apply(this, [func.bind(this), delay]);
			cancel = () => clearTimeout(invokeTo);
		} else {
			let invokeId: number;
			let delta = 0;
			const startTime = new Date().getTime();
			const loop = () => {
				delta = new Date().getTime() - startTime;
				if (delta >= delay) {
					func.apply(this);
					cancel();
				} else {
					invokeId = requestAnimationFrame(loop);
					cancel = () => cancelAnimationFrame(invokeId);
				}
			};
			invokeId = requestAnimationFrame(loop);
			cancel = () => cancelAnimationFrame(invokeId);
		}
	}
	return cancel;
}

export function throttle(this: any, func: Function, limit: number) {
	let waiting = false;
	return (...args: []) => {
		// invoke only if not waiting
		if (!waiting) {
			func.apply(this, args);
			waiting = true;
			invokeAfter(() => {
				// reset waiting after limit
				waiting = false;
			}, limit);
		}
	};
}

export function debounce(this: any, func: Function, delay: number) {
	let inDebounce: (() => void) | undefined;
	return (...args: []) => {
		// If debounce is waiting to happen, just cancel the old one
		if (inDebounce) inDebounce();
		inDebounce = invokeAfter(() => { func.apply(this, args); inDebounce = undefined; }, delay);
	};
}

export function calculateBorderSize(
	selector: string,
	borderSize: number,
	offsetSize: number,
	renderedAsThumbnail?: boolean,
	thumbnailSize?: number,
): number {
	const viewPort = document.querySelector(selector);
	let scaleValue;
	let borderSizeToUse = 0;

	if (viewPort?.clientWidth && !renderedAsThumbnail) {
		scaleValue = viewPort.clientWidth / 1920;
	} else if (viewPort?.clientWidth && renderedAsThumbnail && thumbnailSize) {
		scaleValue = thumbnailSize / 1920;
	}
	if (scaleValue) {
		borderSizeToUse = (borderSize + offsetSize) * scaleValue;
	}

	return borderSizeToUse < 0.75 ? 0.75 : borderSizeToUse;
}

export function dbDateToObject(dbDate: string): IDateObjectProps | null {
	const localeStringDate = new Date(dbDate).toLocaleString(
		navigator.language,
		{
			month: 'short',
			day: 'numeric',
			weekday: 'short',
			hour: 'numeric',
			minute: '2-digit',
		},
	);

	if (localeStringDate.toString() === 'Invalid Date') {
		return null;
	}

	const firstSplit = localeStringDate.replaceAll(',', '').split(' ');

	if (firstSplit.length !== 5) {
		return null;
	}

	const hourMinute = firstSplit?.[3].split(':');

	// Approach: better to return null than not have part of a date
	if (
		hourMinute.length !== 2
		|| !firstSplit[0]
		|| !firstSplit[2]
		|| !firstSplit[1]
		|| !hourMinute[0]
		|| !hourMinute[1]
		|| !firstSplit[4]
	) {
		return null;
	}

	return {
		weekday: firstSplit[0],
		day: firstSplit[2],
		month: firstSplit[1],
		hour: hourMinute[0],
		minute: hourMinute[1],
		hourType: firstSplit[4],
	};
}

export function dayFirstStartEndDate(
	startDate: IDateObjectProps | null,
	endDate: IDateObjectProps | null,
	noDateStringMessage?: string,
): string {
	if (startDate && endDate) {
		if (startDate.day === endDate.day) {
			const dateStringStart = `${startDate.weekday}, ${startDate.day} ${startDate.month}, `;

			let dateStringEnd = '';

			if (startDate.hourType === endDate.hourType) {
				dateStringEnd = `${startDate.hour}:${startDate.minute}-${endDate.hour}:${endDate.minute} ${startDate.hourType}`;
			} else {
				dateStringEnd = `${startDate.hour}:${startDate.minute} ${startDate.hourType}-${endDate.hour}:${endDate.minute} ${endDate.hourType}`;
			}

			return `${dateStringStart}${dateStringEnd}`;
		}
		const startDateTime = `${startDate.weekday}, ${startDate.day} ${startDate.month}, ${startDate.hour}:${startDate.minute} ${startDate.hourType}`;
		const endDateTime = `${endDate.weekday}, ${endDate.day} ${endDate.month}, ${endDate.hour}:${endDate.minute} ${endDate.hourType}`;

		return `${startDateTime} - ${endDateTime}`;
	} if (startDate) {
		return `${startDate.weekday}, ${startDate.day} ${startDate.month}, ${startDate.hour}:${startDate.minute} ${startDate.hourType}`;
	} if (endDate) {
		return `${endDate.weekday}, ${endDate.day} ${endDate.month}, ${endDate.hour}:${endDate.minute} ${endDate.hourType}`;
	}

	return noDateStringMessage ?? '';
}

export function truncateTextBeforeSpace(input: string, maxLength: number): string {
	let truncatedString = input;

	if (input.length > maxLength && maxLength > 0) {
		let end = maxLength - 1;

		for (let charPos = maxLength; charPos >= 0; charPos -= 1) {
			const char = input[charPos];

			// Might consider the definition of a space / whitespace
			if (char === ' ') {
				end = charPos;
				break;
			}
		}

		truncatedString = input.slice(0, end);
	}

	return truncatedString;
}

export function isVideo(urlOrType: string): boolean {
	if (!urlOrType) return false;
	const videoTypes = [...VID_TYPES, MEDIA_TYPES.HLS];
	const inputType = urlOrType.includes('.') ? urlOrType.split('.').pop() ?? '' : urlOrType;
	return videoTypes.some((videoType: string) => videoType === inputType.toLowerCase());
}

export function isAudio(urlOrType: string): boolean {
	if (!urlOrType) return false;
	const inputType = urlOrType.includes('.') ? urlOrType.split('.').pop() ?? '' : urlOrType;
	return AUDIO_TYPES.some((audioType: string) => audioType === inputType.toLowerCase());
}

// returns a new array with an item moved
export function arrayMove(array: any[], fromIndex: number, toIndex: number) {
	const newArray = [...array];
	const startIndex = fromIndex < 0 ? newArray.length + fromIndex : fromIndex;
	if (startIndex >= 0 && startIndex < newArray.length) {
		const endIndex = toIndex < 0 ? newArray.length + toIndex : toIndex;
		const [item] = newArray.splice(fromIndex, 1);
		newArray.splice(endIndex, 0, item);
	}
	return newArray;
}

export function fixedNumberFromInput(inputValue: string, fractionDigits: number = 2) {
	return Number((Number(inputValue) || 0).toFixed(fractionDigits));
}

export const getTimeTextForValue = (seconds: number, editing: boolean, inputType: number) => {
	if (
		// @ts-ignore
		(seconds <= 60 && inputType !== DURATION_INPUT_TYPES.MINUTES)
		|| inputType === DURATION_INPUT_TYPES.SECONDS
	) {
		return `${seconds}${editing ? '' : ' s'}`;
	}

	const min = Math.floor(Number(seconds) / 60);
	const sec = Math.ceil(Number(seconds) - (min * 60));
	return `${min < 10 ? `0${min}` : min}:${sec < 10 ? `0${sec}` : sec}`;
};

export function getUserProfileWithId(
	id: string | number,
	connectedUsers: ConnectedUser[],
	invitations: Invitation[],
): ConnectedUser | Profile {
	// first check the connectedUsers array
	let userFromId: ConnectedUser | Invitation | undefined = connectedUsers.find(
		(user: ConnectedUser) => user.gr_user === id,
	);
	if (userFromId) return userFromId;

	// if the user is not in the room, check the invitations array
	userFromId = invitations.find((invite: Invitation) => invite.profile.gr_user === id);
	if (userFromId) {
		return userFromId.profile;
	}
	return {
		email: '',
		first_name: '',
		last_name: '',
		avatar: '',
		poster: '',
		job_title: '',
		gr_user: 0,
		picture: '',
		title: '',
	};
}

export function hasSpecialCharacters(stringToCheck: string): boolean {
	return stringToCheck.split('').some((char) => ':,;'.indexOf(char) >= 0);
}

export function isValidRecordingFilename(input: string): boolean {
	// eslint-disable-next-line no-control-regex
	const nonISO8859Regex = /[&+?<>#%"~|]|[^\x00-\xFF]/;
	return !nonISO8859Regex.test(input);
}

// takes array of media urls and determines if at least one of them is a video
export function mediaDeckHasVideo(images: string[]): boolean {
	return images.some((image: string) => isVideo(image));
}

// Takes the old hex format and changes it to the new ie #ffffff -> #FFFFFF
export function formatHex(hex: string): string {
	return `#${hex.slice(1).toUpperCase()}`;
}

export function getBrowserType(): string {
	const userAgent = navigator.userAgent.toLowerCase();

	if (userAgent.includes('chrome') && (window as any).chrome) {
		return BROWSER_TYPES.CHROME;
	}
	if (userAgent.includes('firefox')) {
		return BROWSER_TYPES.FIREFOX;
	}
	if (userAgent.includes('trident')) {
		return BROWSER_TYPES.INTERNET_EXPLORER;
	}
	if (userAgent.includes('safari')) {
		return BROWSER_TYPES.SAFARI;
	}
	if (userAgent.includes('edge')) {
		return BROWSER_TYPES.MICROSOFT_EDGE;
	}

	return BROWSER_TYPES.UNKNOWN;
}

export function subtract(a: unknown, b: unknown): number {
	// Account for NaN
	return (Number(a) - Number(b)) || 0;
}

export function valueMinMax(
	value: unknown,
	min: number,
	max: number,
	isInfinityMin: boolean = true,
): number {
	const numberValue = Number(value);

	if (Number.isNaN(numberValue) || numberValue < min) {
		return min;
	}

	if (numberValue === Infinity) {
		if (isInfinityMin) {
			return min;
		}

		return max;
	}

	if (numberValue > max) {
		return max;
	}

	return numberValue;
}

// It's simple, but I used it more than once so figured why not
export function zeroAndUp(input: number): number {
	return input > 0 ? input : 0;
}

// Add a primitive to an array (unique by default)
export function arrayAdd<T extends string | number | boolean>(
	array: T[],
	element: T,
	isUnique: boolean = true,
): T[] {
	if (!isUnique) {
		return [...array, element];
	}

	return Array.from(new Set([...array, element]));
}

export function multiplierFibonacci(currentMultiplier: number, resultMultipler: number = 1) {
	let [a, b, temp] = [1, 0, 0];

	while (a <= (currentMultiplier / resultMultipler)) {
		temp = a;
		a += b;
		b = temp;
	}

	return a * resultMultipler;
}

/**
 * return a string for copied trim clip
 * @param originalId: number - the id of the original media item
 * @param originalName: string - the name of the original media item
 * @param mediaItems: MediaItem[] - array of media items
 */
export function getTrimSaveCopyName(
	originalId: number,
	mediaItems: MediaItem[],
): string {
	const found = mediaItems.filter((m) => m.original_id === originalId);
	const original = mediaItems.find((m) => m.id === originalId);

	// grr no original... seems bad man
	if (!original || (!original.display_name && !original.original_name)) {
		return found.length === 0
			? 'Trimmed Clip'
			: `Trimmed Clip ${found.length + 1}`;
	}

	const originalName = original.custom_name ?? original.original_name;
	return `${originalName} Trimmed${found.length > 0 ? ` ${found.length + 1}` : ''}`;
}

export function validateFile(incomingFile: File | FileList): boolean {
	const file = incomingFile instanceof FileList ? incomingFile[0] : incomingFile;
	const extension = file.name.split('.').pop() as string;
	if (
		!SUPPORTED_CHAT_FILE_FORMATS.FILE_TYPES?.includes(extension)
		|| !SUPPORTED_CHAT_FILE_FORMATS.MIME_TYPES?.includes(file.type)
	) {
		return false;
	} if (file.size > MAX_FILE_SIZE) {
		return false;
	}
	return true;
}

// Upsert for generic object array
export function arrayUpsert<T extends object>(
	inputArray: T[],
	element: T,
	key: keyof T,
): T[] {
	const inputArrayCopy = [...inputArray];
	const index = inputArrayCopy.findIndex(
		(arrayObject) => arrayObject[key] === element[key],
	);

	if (index !== -1) {
		inputArrayCopy[index] = element;
	} else {
		inputArrayCopy.push(element);
	}

	return inputArrayCopy;
}

export function isZeroOrTruthy(input: number | string | undefined | null): boolean {
	if (input === 0) {
		return true;
	}
	return !!input;
}

export function menuPositionToStyle({
	top,
	right,
	bottom,
	left,
}: CssPosition): CSSProperties {
	return {
		...(top && { top: `${top}px` }),
		...(right && { right: `${right}px` }),
		...(bottom && { bottom: `${bottom}px` }),
		...(left && { left: `${left}px` }),
	};
}

export function getUsersName(user: GreenroomUser): string {
	if (user.first_name && user.last_name) {
		return `${user.first_name} ${user.last_name}`;
	} if (user.email) {
		return user.email;
	}
	return 'Unknown User';
}

export function timeToMilliseconds(
	hours?: number,
	minutes?: number,
	seconds?: number,
	milliseconds?: number,
) {
	let totalMilliseconds = 0;

	if (hours) {
		totalMilliseconds += (1000 * 60 * 60 * hours);
	}

	if (minutes) {
		totalMilliseconds += (1000 * 60 * minutes);
	}

	if (seconds) {
		totalMilliseconds += (1000 * seconds);
	}

	if (milliseconds) {
		totalMilliseconds += milliseconds;
	}

	return totalMilliseconds;
}

// Build object that can be used to display chat message anywhere
export function getDisplayChatMessage(
	chatMessage: GeneralEventsMessage,
	shouldStripHtml: boolean = false,
): DisplayChatMessage {
	const {
		first_name: firstName,
		last_name: lastName,
		comment: message,
		message_type: messageType,
	} = chatMessage;
	const eventsMessageChat = messageType === EMessageTypes.Chat
		? chatMessage as EventsMessageChat
		: null;
	const eventsMessageComment = messageType === EMessageTypes.QuestionComment
		? chatMessage as EventsMessageComment
		: null;

	// Build name string
	let name = [firstName ?? '', lastName ?? ''].join(' ').trim();

	// If there is no name, check if the user is a moderator and label if so
	if (!name) {
		let isModerator: boolean = false;

		if (eventsMessageChat?.is_moderator) {
			// This check handles chat messages
			isModerator = true;
		} else if (eventsMessageComment) {
			// This check handles moderator replies
			const {
				moderator,
				moderator_reply_to_comment_id: originalCommentId,
				moderator_reply_to_question_id: originalQuestionId,
			} = eventsMessageComment;

			if (moderator && (originalCommentId || originalQuestionId)) {
				isModerator = true;
			}
		}

		if (isModerator) {
			name = 'Moderator';
		} else {
			name = 'Anonymous';
		}
	}

	return {
		name,
		message: shouldStripHtml ? stripHtml(message) : message,
		messageType,
	};
}

export function handleClickJacking() {
	if (self === top) {
		document.documentElement.style.display = 'block';
	} else if (top?.location) {
		top.location = self.location;
	}
}

export function shouldEnableUserMedia() {
	return window.location.pathname !== '/producer'
		&& window.location.pathname !== '/producer/'
		&& window.location.pathname !== '/';
}

export function getChannelLogoFromMeeting(meeting: Meeting | Channel | null): string {
	// typescript fun
	const isMeeting = (m: Meeting | Channel | null):
		m is Meeting => (m as Meeting)?.logos !== undefined;

	if (isMeeting(meeting) && meeting?.logos) {
		return meeting?.logos?.color
			?? meeting?.logos?.white
			?? '';
	}
	return meeting?.logo_color
		?? meeting?.logo_white
		?? '';
}

export function whichChannelLogoToUse(
	logoWhite: string | undefined,
	logoColor: string | undefined,
): string {
	return logoColor ?? logoWhite ?? '';
}

interface PlatformAuthUrlProps {
	userEmail?: string;
	channelId?: string;
	customRoomId?: string;
	customRedirectOrigin?: string;
}

// We are building a link to Platform in order to log in
// ${platformEnv}/authentication/presenter/sign-in/${userEmail}/${channelId}?redirect=${encodedUrl}
export function platformAuthUrl({
	userEmail,
	channelId,
	customRoomId,
	customRedirectOrigin,
}: PlatformAuthUrlProps = {}): string {
	const platformUrl = process.env.REACT_APP_PLATFORM_URL || 'https://admin.brandlive.com';

	let loginLink = `${platformUrl}/authentication/presenter/sign-in`;

	if (userEmail) {
		loginLink += `/${userEmail}`;

		if (channelId) {
			loginLink += `/${channelId}`;
		}
	}

	const url = new URL(window.location.href);
	const roomId = customRoomId || url
		.pathname
		.split('/')
		.filter(Boolean)
		.pop()
		|| '';
	const params = new URLSearchParams({
		redirect: `${customRedirectOrigin || url.origin}/oauth/${roomId}`,
	});

	return `${loginLink}?${params.toString()}`;
}

export function doesEnvVarExist(
	envVarName: string,
	sentFrom: string | null = null,
): boolean {
	const message: string | null = process.env[envVarName]
		? null
		: `Env var "${envVarName}" is missing${sentFrom ? ` in ${sentFrom}` : ''}.`;

	if (message === null) {
		return true;
	}

	logger.warn(message);

	if (process.env.NODE_ENV && process.env.NODE_ENV !== 'production') {
		// eslint-disable-next-line no-console
		console.warn(message);
	}

	return false;
}

export const getFeedStyle = (
	layout: string,
	position: number,
	shotStyle: AppSettings,
	isThumbnail: boolean = false,
	thumbnailWidth: number = 1920,
) => {
	const {
		presenterVideoBorderOn,
		presenterVideoBorderSize,
		presenterVideoBorderOpacity,
		presenterVideoBorderPosition,
		presenterVideoBorderColor,
		presenterVideoRadius,
	} = shotStyle;

	const style: CSSProperties = {
		display: layout === V_LAYOUTS.TWO.FULL_TWO && position !== 1 ? 'none' : 'block',
		borderRadius: '0rem',
		boxShadow: 'none',
	};

	const insideStyle: CSSProperties = {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		borderRadius: '0rem',
		boxShadow: 'none',
	};

	// border radius
	const isPlain = (
		PLAIN_LAYOUTS.includes(layout) // layouts without styling
		|| (PIP_LAYOUTS.includes(layout) && position === 1) // large feed in PIP layout
	);
	if (!isPlain) {
		const borderRadius = isThumbnail
			? `${convertPxToRem(presenterVideoRadius * ((thumbnailWidth) / 550))}rem`
			: presenterVideoRadius;
		style.borderRadius = borderRadius;
		insideStyle.borderRadius = borderRadius;
	}

	// border size and color
	const showBorder = presenterVideoBorderOn && !isPlain;
	if (showBorder) {
		const [r, g, b] = hexToRgb(presenterVideoBorderColor);
		const color = `rgba(${r}, ${g}, ${b}, ${presenterVideoBorderOpacity})`;
		const borderSize = convertPxToRem(
			calculateBorderSize(
				isThumbnail ? '.video-container-presenter' : '.main-video',
				presenterVideoBorderSize,
				0,
				isThumbnail,
				isThumbnail ? thumbnailWidth : undefined,
			),
		);
		const boxShadow = `0 0 0 ${borderSize}rem ${color}`;
		if (presenterVideoBorderPosition === 'outside') style.boxShadow = boxShadow;
		if (presenterVideoBorderPosition === 'inside') insideStyle.boxShadow = `inset ${boxShadow}`;
	}

	return {
		style,
		insideStyle,
	};
};

export const safeIntFromPx = (unknownProp: any): number => {
	if (typeof unknownProp === 'string') {
		const numStr = parseInt(unknownProp.replace('px', ''), 10);
		return Number.isNaN(numStr) ? 0 : numStr;
	}
	return typeof unknownProp === 'number' ? unknownProp : 0;
};
